import type { MaybeRef } from '@/types/utilityTypes'
import { useQuery } from '@tanstack/vue-query'
import { unravel } from '@/service/jsonApi'
import { computed, unref } from 'vue'
import { getUserUpload, listUserUploads } from './userUploadApi'
import {
  QUERY_KEY_USER_UPLOAD,
  QUERY_KEY_USER_UPLOADS_LIST,
} from './userUploadKeys'
import type { UserUploadsListQueryOptions } from './userUploadTypes'

const DEFAULT_POLL_INTERVAL_MS = 15000

export const useUserUploadQuery = (
  companyNameKey: MaybeRef<string | undefined>,
  uploadId: MaybeRef<string | undefined>,
  options?: { pollInterval?: number }
) => {
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS

  return useQuery({
    queryKey: [QUERY_KEY_USER_UPLOAD, companyNameKey, uploadId],
    queryFn: () => {
      const key = unref(companyNameKey)
      const id = unref(uploadId)
      if (key === undefined || id === undefined) {
        throw new Error(
          'useUserUploadQuery: companyNameKey and uploadId are required but were undefined when queryFn ran'
        )
      }
      return getUserUpload({ companyNameKey: key, uploadId: id })
    },
    select: (data) => unravel(data),
    enabled: computed(() => !!unref(companyNameKey) && !!unref(uploadId)),
    refetchInterval: (query) => {
      if (!query.state.data) return false
      const { state } = query.state.data.data.attributes
      return ['initialized', 'started'].includes(state) ? pollInterval : false
    },
  })
}

export const useUserUploadsListQuery = (
  companyNameKey: MaybeRef<string | undefined>,
  options: UserUploadsListQueryOptions = {}
) => {
  const { filterState, sort, pageSize, pageNumber } = options

  return useQuery({
    queryKey: [
      QUERY_KEY_USER_UPLOADS_LIST,
      companyNameKey,
      filterState,
      sort,
      pageSize,
      pageNumber,
    ],
    queryFn: () => {
      const key = unref(companyNameKey)
      if (key === undefined) {
        throw new Error(
          'useUserUploadsListQuery: companyNameKey is required but was undefined when queryFn ran'
        )
      }
      return listUserUploads({
        companyNameKey: key,
        filterState: unref(filterState),
        sort: unref(sort),
        pageSize: unref(pageSize),
        pageNumber: unref(pageNumber),
      })
    },
    select: (data) => unravel(data),
    enabled: computed(() => !!unref(companyNameKey)),
  })
}
